(() => {
  const ROOT_SELECTOR = 'product-info.vag-main-product';
  const BUNDLE_SELECTOR = '[data-vag-bundle]';
  const PAY_NOW_SELECTOR = '[data-vag-pay-now]';
  const FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';

  const HIDDEN_QTY_SELECTOR = '[data-vag-main-qty]';
  const QTY_INPUT_SELECTOR = '[data-vag-qty-input]';
  const QTY_MINUS_SELECTOR = '[data-vag-qty-minus]';
  const QTY_PLUS_SELECTOR = '[data-vag-qty-plus]';
  const PACK_RADIO_SELECTOR = '.vag-mp__bundle-radio';
  const SUMMARY_SELECTOR = '[data-vag-bundle-summary]';
  const ERROR_SELECTOR = '.vag-mp__pay-now-error';

  // ✅ Fallbacks (los tuyos)
  const DEFAULT_CODE_2 = 'descuento25';
  const DEFAULT_CODE_3 = 'descuento35';

  const busyRoots = new WeakSet();
  const observers = new WeakMap();

  function getRoot(el) {
    return el ? el.closest(ROOT_SELECTOR) : null;
  }

  function getForm(root) {
    return root ? root.querySelector(FORM_SELECTOR) : null;
  }

  function showError(root, message) {
    const box = root ? root.querySelector(ERROR_SELECTOR) : null;
    if (!box) return;
    box.textContent = message || 'Ha ocurrido un error. Inténtalo de nuevo.';
    box.hidden = false;
  }

  function clearError(root) {
    const box = root ? root.querySelector(ERROR_SELECTOR) : null;
    if (!box) return;
    box.textContent = '';
    box.hidden = true;
  }

  function toPath(urlOrPath, fallback = '/checkout') {
    if (!urlOrPath) return fallback;
    try {
      const u = new URL(urlOrPath, window.location.origin);
      return (u.pathname || '') + (u.search || '') + (u.hash || '');
    } catch (_) {
      return urlOrPath;
    }
  }

  function normalizeRootUrl(rootUrl) {
    let r = rootUrl || '/';
    if (!r.startsWith('/')) r = '/' + r;
    if (!r.endsWith('/')) r = r + '/';
    return r;
  }

  function dispatchValueEvents(el) {
    try {
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    } catch (_) {}
  }

  function clampToRules(qty, rules) {
    let q = Number.isFinite(qty) ? qty : 1;
    q = Math.max(1, Math.floor(q));

    const min = Number.isFinite(rules.min) ? rules.min : 1;
    const step = Number.isFinite(rules.step) ? rules.step : 1;
    const max = Number.isFinite(rules.max) ? rules.max : null;

    if (q < min) q = min;

    if (step > 1) {
      const delta = q - min;
      const mod = delta % step;
      if (mod !== 0) q = q + (step - mod);
    }

    if (max != null && q > max) q = max;

    return q;
  }

  function readBundleState(root) {
    const bundle = root ? root.querySelector(BUNDLE_SELECTOR) : null;
    if (!bundle) return null;

    const checked = bundle.querySelector(`${PACK_RADIO_SELECTOR}:checked`);
    const packUnits = checked ? parseInt(checked.value, 10) : 1;

    const qtyInput = bundle.querySelector(QTY_INPUT_SELECTOR);
    const packsCount = qtyInput ? parseInt(qtyInput.value, 10) : 1;

    const rules = {
      min: parseInt(bundle.dataset.min || '1', 10),
      step: parseInt(bundle.dataset.step || '1', 10),
      max: bundle.dataset.max ? parseInt(bundle.dataset.max, 10) : null
    };

    return {
      bundle,
      packUnits: Number.isFinite(packUnits) && packUnits > 0 ? packUnits : 1,
      packsCount: Number.isFinite(packsCount) && packsCount > 0 ? packsCount : 1,
      rules
    };
  }

  function computeDesiredQuantity(root) {
    const state = readBundleState(root);
    if (!state) {
      const hidden = root ? root.querySelector(HIDDEN_QTY_SELECTOR) : null;
      const q = hidden ? parseInt(hidden.value, 10) : 1;
      return clampToRules(Number.isFinite(q) ? q : 1, { min: 1, step: 1, max: null });
    }
    const rawQty = state.packUnits * state.packsCount;
    return clampToRules(rawQty, state.rules);
  }

  function applyQuantityEverywhere(root, qty) {
    if (!root) return;

    root.dataset.vagQty = String(qty);

    const hidden = root.querySelector(HIDDEN_QTY_SELECTOR);
    if (hidden) {
      hidden.value = String(qty);
      dispatchValueEvents(hidden);
    }

    root.querySelectorAll('input[name="quantity"], select[name="quantity"]').forEach((el) => {
      if (el.matches(QTY_INPUT_SELECTOR)) return;
      el.value = String(qty);
      dispatchValueEvents(el);
    });

    // Releasit / COD heurística
    root.querySelectorAll([
      'input[type="number"][id*="releasit"]',
      'input[type="hidden"][id*="releasit"]',
      'input[type="number"][name*="releasit"]',
      'input[type="hidden"][name*="releasit"]',
      'input[type="number"][class*="releasit"]',
      'input[type="hidden"][class*="releasit"]',
      '[data-releasit-quantity]',
      '[data-cod-quantity]'
    ].join(',')).forEach((el) => {
      if (el.matches(QTY_INPUT_SELECTOR)) return;
      el.value = String(qty);
      dispatchValueEvents(el);
    });

    try {
      root.dispatchEvent(new CustomEvent('vag:quantity-change', { bubbles: true, detail: { quantity: qty } }));
    } catch (_) {}
  }

  function syncBundleUI(root) {
    const state = readBundleState(root);
    if (!state) return;

    const desiredQty = computeDesiredQuantity(root);

    const summary = state.bundle.querySelector(SUMMARY_SELECTOR);
    if (summary) summary.textContent = `${desiredQty} unidad${desiredQty === 1 ? '' : 'es'} en total`;

    applyQuantityEverywhere(root, desiredQty);
    ensureObserver(root);
  }

  function ensureObserver(root) {
    if (!root || observers.has(root)) return;

    const obs = new MutationObserver(() => {
      const q = parseInt(root.dataset.vagQty || '1', 10);
      if (Number.isFinite(q) && q > 0) applyQuantityEverywhere(root, q);
    });

    obs.observe(root, { childList: true, subtree: true });
    observers.set(root, obs);
  }

  function pickDiscountCode(root, totalUnits) {
    const bundle = root ? root.querySelector(BUNDLE_SELECTOR) : null;

    // lee del bloque (si lo rellenaste)
    const code2 = bundle ? (bundle.dataset.code2 || '').trim() : '';
    const code3 = bundle ? (bundle.dataset.code3 || '').trim() : '';

    // fallback a tus códigos si está vacío
    const final2 = code2 || DEFAULT_CODE_2;
    const final3 = code3 || DEFAULT_CODE_3;

    // ✅ reglas exactas
    if (totalUnits >= 3) return final3;
    if (totalUnits === 2) return final2;
    return '';
  }

  function withDiscountParam(checkoutPath, code) {
    if (!code) return checkoutPath;
    if (checkoutPath.includes('discount=')) return checkoutPath;
    return checkoutPath + (checkoutPath.includes('?') ? '&' : '?') + 'discount=' + encodeURIComponent(code);
  }

  async function addToCartAndCheckout(root, triggerEl) {
    if (!root || busyRoots.has(root)) return;

    const form = getForm(root);
    if (!form) {
      showError(root, 'No encuentro el formulario del producto. Asegúrate de tener el bloque “Buy buttons”.');
      return;
    }

    clearError(root);

    const idInput = form.querySelector('input[name="id"]');
    const variantId = idInput && idInput.value ? String(idInput.value) : '';
    if (!variantId) {
      showError(root, 'Selecciona una variante antes de comprar.');
      return;
    }

    const totalUnits = computeDesiredQuantity(root);
    if (!totalUnits || totalUnits < 1) {
      showError(root, 'La cantidad no es válida.');
      return;
    }

    applyQuantityEverywhere(root, totalUnits);
    ensureObserver(root);

    busyRoots.add(root);
    if (triggerEl) {
      triggerEl.setAttribute('aria-busy', 'true');
      triggerEl.disabled = true;
    }

    try {
      const cartAddUrl = root.dataset.cartAddUrl || '/cart/add.js';
      const checkoutPath = toPath(root.dataset.checkoutUrl || '/checkout', '/checkout');
      const rootUrl = normalizeRootUrl(root.dataset.rootUrl || (window.Shopify?.routes?.root || '/'));

      const fd = new FormData(form);
      fd.set('quantity', String(totalUnits));

      const res = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd,
        credentials: 'same-origin'
      });

      if (!res.ok) {
        let data = null;
        try { data = await res.json(); } catch (_) {}
        throw new Error((data && (data.description || data.message)) || 'No se pudo añadir al carrito.');
      }

      const code = pickDiscountCode(root, totalUnits);

      // ✅ Ultra fiable: /discount/CODE?redirect=/checkout?discount=CODE
      if (code) {
        const redirectTarget = withDiscountParam(checkoutPath, code);
        const discountUrl =
          `${rootUrl}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirectTarget)}`;
        window.location.href = discountUrl;
        return;
      }

      window.location.href = checkoutPath;
    } catch (err) {
      showError(root, err?.message || 'Error al comprar.');
    } finally {
      busyRoots.delete(root);
      if (triggerEl) {
        triggerEl.removeAttribute('aria-busy');
        triggerEl.disabled = false;
      }
    }
  }

  document.addEventListener('click', (e) => {
    const payBtn = e.target.closest(PAY_NOW_SELECTOR);
    if (payBtn) {
      const root = getRoot(payBtn);
      if (!root) return;
      e.preventDefault();
      addToCartAndCheckout(root, payBtn);
      return;
    }

    const minus = e.target.closest(QTY_MINUS_SELECTOR);
    const plus = e.target.closest(QTY_PLUS_SELECTOR);

    if (minus || plus) {
      const root = getRoot(minus || plus);
      if (!root) return;

      const bundle = root.querySelector(BUNDLE_SELECTOR);
      if (!bundle) return;

      const input = bundle.querySelector(QTY_INPUT_SELECTOR);
      if (!input) return;

      const current = parseInt(input.value, 10);
      const next = (Number.isFinite(current) ? current : 1) + (plus ? 1 : -1);
      input.value = String(Math.max(1, next));

      syncBundleUI(root);
    }
  });

  document.addEventListener('change', (e) => {
    if (e.target && e.target.matches(PACK_RADIO_SELECTOR)) {
      const root = getRoot(e.target);
      if (!root) return;
      syncBundleUI(root);
    }
  });

  document.addEventListener('input', (e) => {
    if (e.target && e.target.matches(QTY_INPUT_SELECTOR)) {
      const root = getRoot(e.target);
      if (!root) return;
      syncBundleUI(root);
    }
  });

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || !form.matches(FORM_SELECTOR)) return;

    const root = getRoot(form);
    if (!root) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const payBtn = root.querySelector(PAY_NOW_SELECTOR);
    addToCartAndCheckout(root, payBtn || form.querySelector('button[type="submit"]'));
  }, true);

  function init() {
    document.querySelectorAll(`${ROOT_SELECTOR} ${BUNDLE_SELECTOR}`).forEach((bundle) => {
      const root = getRoot(bundle);
      if (!root) return;
      syncBundleUI(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();